import { Calculator, CreditCard, FolderKanban, Pen, RefreshCcw, Trash2, Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AppCard from "../../components/AppCard";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import { useDemo } from "../../providers/DemoProvider";
import type { ConnectionId, GroupId, PlatformType } from "../../types/demo";

type Connector = {
  platform: PlatformType;
  description: string;
  comingSoon?: boolean;
};

type ConnectorCategory = {
  title: string;
  description: string;
  connectors: Connector[];
};

type PendingXeroPayload = {
  platform: "xero";
  tokenData: {
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
    scope?: string;
    token_type?: string;
    id_token?: string;
  };
  tenants: Array<{
    tenantId: string;
    tenantName?: string;
    tenantType?: string;
  }>;
};

const platformLabels: Record<PlatformType, string> = {
  xero: "Xero",
  myob: "MYOB",
  quickbooks: "QuickBooks",
  sage: "Sage",
  hubspot: "HubSpot",
  stripe: "Stripe",
  procore: "Procore",
  aroflo: "AroFlo",
};

const categoryIcons: Record<string, typeof Calculator> = {
  Accounting: Calculator,
  Sales: Users,
  Payment: CreditCard,
  "Project Management": FolderKanban,
};

const platformColors: Record<PlatformType, string> = {
  xero: "bg-blue-500/10 text-blue-400",
  myob: "bg-indigo-500/10 text-indigo-400",
  quickbooks: "bg-emerald-500/10 text-emerald-500",
  sage: "bg-emerald-500/10 text-emerald-500",
  hubspot: "bg-amber-50 text-amber-500",
  stripe: "bg-indigo-500/10 text-indigo-400",
  procore: "bg-amber-50 text-amber-500",
  aroflo: "bg-blue-500/10 text-blue-400",
};

const platformInitials: Record<PlatformType, string> = {
  xero: "X",
  myob: "M",
  quickbooks: "QB",
  sage: "S",
  hubspot: "H",
  stripe: "S",
  procore: "P",
  aroflo: "A",
};

const connectorCategories: ConnectorCategory[] = [
  {
    title: "Accounting",
    description: "Connect your accounting platforms for financial data and reporting",
    connectors: [
      { platform: "xero", description: "Connect your Xero accounting data" },
      { platform: "myob", description: "Connect your MYOB accounting data" },
      { platform: "quickbooks", description: "Connect your QuickBooks accounting data" },
      { platform: "sage", description: "Connect your Sage accounting data", comingSoon: true },
    ],
  },
  {
    title: "Sales",
    description: "Connect your CRM and sales platforms for pipeline analysis",
    connectors: [{ platform: "hubspot", description: "Connect your HubSpot CRM data for sales pipeline analysis" }],
  },
  {
    title: "Payment",
    description: "Connect your payment platforms for transaction analysis",
    connectors: [{ platform: "stripe", description: "Sync payment and transaction data" }],
  },
  {
    title: "Project Management",
    description: "Connect your project management platforms for construction and project insights",
    connectors: [
      { platform: "procore", description: "Connect your Procore construction management data" },
      { platform: "aroflo", description: "Connect your AroFlo field service management data" },
    ],
  },
];

const DialogShell = ({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">{title}</h3>
            {subtitle && <p className="mt-1 text-xs font-medium text-slate-400">{subtitle}</p>}
          </div>
          <button className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[72vh] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

export default () => {
  const { state, connectPlatform, completeOAuthConnection, renameConnection, disconnectConnection, saveGroup, deleteGroup } = useDemo();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"connections" | "groups">("connections");
  const [connecting, setConnecting] = useState<PlatformType | null>(null);
  const [isConnectorDialogOpen, setConnectorDialogOpen] = useState(false);
  const [isGroupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<GroupId | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<ConnectionId | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<GroupId | null>(null);
  const [renamingConnectionId, setRenamingConnectionId] = useState<ConnectionId | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [pendingXeroPayload, setPendingXeroPayload] = useState<PendingXeroPayload | null>(null);

  const callbackPlatform = searchParams.get("platform");
  const callbackStatus = searchParams.get("status");
  const callbackReason = searchParams.get("reason");
  const callbackCount = searchParams.get("count");
  const platformName = callbackPlatform ? platformLabels[callbackPlatform as PlatformType] ?? callbackPlatform : null;
  const callbackMessage = callbackPlatform && callbackStatus === "connected"
    ? `${platformName} connected${callbackCount ? ` (${callbackCount} tenant${callbackCount === "1" ? "" : "s"})` : ""}.`
    : callbackPlatform && callbackStatus === "error"
    ? `${platformName} connection failed${callbackReason ? `: ${callbackReason}` : "."}`
    : null;

  const groupForm = useForm<{ name: string; description: string; connectionIds: ConnectionId[] }>({
    defaultValues: {
      name: "",
      description: "",
      connectionIds: [],
    },
  });

  const renameForm = useForm<{ name: string }>({
    defaultValues: {
      name: "",
    },
  });

  const pendingConnectionForm = useForm<{ name: string; tenantId: string }>({
    defaultValues: {
      name: "",
      tenantId: "",
    },
  });

  useEffect(() => {
    if (!location.hash.startsWith("#")) {
      return;
    }

    const hashParams = new URLSearchParams(location.hash.slice(1));
    const rawOauth = hashParams.get("oauth");
    if (!rawOauth) {
      return;
    }

    try {
      const payload = JSON.parse(rawOauth) as PendingXeroPayload;
      if (payload.platform !== "xero" || payload.tenants.length === 0 || !payload.tokenData.access_token) {
        throw new Error("Invalid Xero OAuth payload");
      }

      const primaryTenant = payload.tenants[0];
      setPendingXeroPayload(payload);
      pendingConnectionForm.reset({
        name: primaryTenant?.tenantName ? `${primaryTenant.tenantName} Xero` : `Xero ${state.connections.filter(connection => connection.platform === "xero").length + 1}`,
        tenantId: primaryTenant?.tenantId ?? "",
      });
    } catch {
      setConnectionError("We received the Xero token, but the callback payload could not be read.");
    }

    navigate(`${location.pathname}${location.search}`, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate, pendingConnectionForm, state.connections]);

  const groupsById = Object.fromEntries(state.groups.map(group => [group.id, group])) as Record<GroupId, (typeof state.groups)[number]>;

  const runConnect = async (platform: PlatformType) => {
    setConnecting(platform);
    setConnectionError(null);

    try {
      const sequence = state.connections.filter(connection => connection.platform === platform).length + 1;
      await connectPlatform(platform, `${platformLabels[platform]} ${sequence}`);
      if (platform !== "xero" && platform !== "procore") {
        setConnecting(null);
      }
    } catch (error) {
      setConnectionError(error instanceof Error ? error.message : `Failed to connect ${platformLabels[platform]}`);
      setConnecting(null);
    }
  };

  const openCreateGroupDialog = () => {
    setEditingGroupId(null);
    groupForm.reset({
      name: "",
      description: "",
      connectionIds: [],
    });
    setGroupDialogOpen(true);
  };

  const editGroup = (id: GroupId) => {
    const group = state.groups.find(item => item.id === id);
    if (!group) {
      return;
    }

    setEditingGroupId(group.id);
    groupForm.reset({
      name: group.name,
      description: group.description ?? "",
      connectionIds: state.connections.filter(connection => connection.groupIds.includes(group.id)).map(connection => connection.id),
    });
    setGroupDialogOpen(true);
  };

  const closeGroupDialog = () => {
    setEditingGroupId(null);
    setGroupDialogOpen(false);
    groupForm.reset({
      name: "",
      description: "",
      connectionIds: [],
    });
  };

  const openRenameDialog = (connectionId: ConnectionId) => {
    const connection = state.connections.find(item => item.id === connectionId);
    if (!connection) {
      return;
    }

    setRenamingConnectionId(connection.id);
    renameForm.reset({ name: connection.name });
  };

  const closeRenameDialog = () => {
    setRenamingConnectionId(null);
    renameForm.reset({ name: "" });
  };

  const renamingConnection = renamingConnectionId
    ? state.connections.find(connection => connection.id === renamingConnectionId) ?? null
    : null;

  const deletingGroup = deletingGroupId ? state.groups.find(group => group.id === deletingGroupId) ?? null : null;
  const selectedPendingTenant = pendingXeroPayload
    ? pendingXeroPayload.tenants.find(tenant => tenant.tenantId === pendingConnectionForm.watch("tenantId")) ?? pendingXeroPayload.tenants[0] ?? null
    : null;

  return (
    <div className="space-y-6">
      <div className="inline-flex rounded-xl border border-slate-200 bg-slate-100 p-1">
        <button
          className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors ${activeTab === "connections" ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
          onClick={() => setActiveTab("connections")}
        >
          Connections
        </button>
        <button
          className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors ${activeTab === "groups" ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
          onClick={() => setActiveTab("groups")}
        >
          Groups
        </button>
      </div>

      {callbackMessage && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${callbackMessage.includes("failed") ? "border-rose-200 bg-rose-50 text-rose-600" : "border-emerald-200 bg-emerald-50 text-emerald-600"}`}>
          {callbackMessage}
        </div>
      )}

      {connectionError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {connectionError}
        </div>
      )}

      {activeTab === "connections" && (
        <AppCard
          title="Integrations"
          subtitle="Connected platforms grouped for quick management"
          className=""
          action={<Button size="sm" className="h-8" onClick={() => setConnectorDialogOpen(true)}>Add Connector</Button>}
        >
          <div className="space-y-6">
            {state.connections.length === 0 && (
              <div className="py-8 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                  <CreditCard className="h-5 w-5 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-500">No active connections</p>
                <p className="mt-1 text-xs text-slate-400">Use "Add Connector" to get started.</p>
              </div>
            )}
            {connectorCategories.map(category => {
              const activeConnectors = category.connectors.filter(connector => state.connections.some(connection => connection.platform === connector.platform));
              if (activeConnectors.length === 0) return null;
              const CategoryIcon = categoryIcons[category.title];
              return (
                <div key={category.title} className="space-y-3">
                  <div className="flex items-center gap-2">
                    {CategoryIcon && <CategoryIcon className="h-4 w-4 text-slate-400" />}
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{category.title}</p>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {activeConnectors.map(connector => {
                      const platformConnections = state.connections.filter(connection => connection.platform === connector.platform);
                      return (
                        <div key={connector.platform} className="rounded-xl border border-slate-200 bg-white p-4">
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5">
                              <div className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold ${platformColors[connector.platform]}`}>
                                {platformInitials[connector.platform]}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{platformLabels[connector.platform]}</p>
                                <p className="text-[11px] text-slate-400">{platformConnections.length} connection{platformConnections.length !== 1 ? "s" : ""}</p>
                              </div>
                            </div>
                            <Badge tone="success" label="Active" size="sm" />
                          </div>

                          <div className="space-y-1.5">
                            {platformConnections.map(connection => {
                              const groupNames = connection.groupIds.map(groupId => groupsById[groupId]?.name).filter(Boolean);
                              return (
                                <div key={connection.id} className="group flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                                  <div className="min-w-0">
                                    <p className="truncate text-xs font-semibold text-slate-800">{connection.name}</p>
                                    <p className="truncate text-[11px] text-slate-400">{groupNames.length > 0 ? groupNames.join(", ") : "Ungrouped"}</p>
                                  </div>
                                  <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                                    <button className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-white hover:text-slate-600" onClick={() => openRenameDialog(connection.id)}>
                                      <Pen className="h-3 w-3" />
                                    </button>
                                    <button className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-white hover:text-rose-500" onClick={() => setDisconnectingId(connection.id)}>
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </AppCard>
      )}

      {activeTab === "groups" && (
        <AppCard
          title="Connection Groups"
          subtitle="Create and manage groups of connections for focused analysis"
          className=""
          action={<Button size="sm" className="h-8" onClick={openCreateGroupDialog}>Create Group</Button>}
        >
          <div className="space-y-2">
            {state.groups.length === 0 && (
              <div className="py-8 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                  <FolderKanban className="h-5 w-5 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-500">No groups yet</p>
                <p className="mt-1 text-xs text-slate-400">Create groups to organize connections for focused analysis.</p>
              </div>
            )}
            {state.groups.map(group => {
              const members = state.connections.filter(connection => connection.groupIds.includes(group.id));
              return (
                <div key={group.id} className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">{group.name}</p>
                    <p className="mt-0.5 text-[11px] text-slate-400">{members.length} connection{members.length !== 1 ? "s" : ""}{group.description ? ` · ${group.description}` : ""}</p>
                  </div>
                  <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <button className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600" onClick={() => editGroup(group.id)}>
                      <Pen className="h-3.5 w-3.5" />
                    </button>
                    <button className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-50 hover:text-rose-500" onClick={() => setDeletingGroupId(group.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </AppCard>
      )}

      {isConnectorDialogOpen && (
        <DialogShell
          title="Add Connector"
          subtitle="Choose platforms by category."
          onClose={() => setConnectorDialogOpen(false)}
        >
          <div className="space-y-5">
            {connectorCategories.map(category => {
              const CategoryIcon = categoryIcons[category.title];
              return (
                <div key={category.title}>
                  <div className="mb-2 flex items-center gap-2">
                    {CategoryIcon && <CategoryIcon className="h-4 w-4 text-slate-400" />}
                    <p className="text-sm font-semibold text-slate-800">{category.title}</p>
                  </div>
                  <div className="space-y-2">
                    {category.connectors.map(connector => {
                      const count = state.connections.filter(connection => connection.platform === connector.platform).length;
                      return (
                        <div key={connector.platform} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${platformColors[connector.platform]}`}>
                              {platformInitials[connector.platform]}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{platformLabels[connector.platform]}</p>
                              <p className="text-xs text-slate-400">{connector.description}</p>
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            {count > 0 && <Badge tone="success" label={`${count} active`} size="sm" />}
                            <Button
                              size="sm"
                              className="h-8"
                              variant={connector.comingSoon ? "outline" : "primary"}
                              disabled={connector.comingSoon || !!connecting}
                              onClick={() => runConnect(connector.platform)}
                            >
                              {connector.comingSoon ? "Coming Soon" : connecting === connector.platform ? <RefreshCcw className="h-4 w-4 animate-spin" /> : count > 0 ? "Add Another" : "Connect"}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogShell>
      )}

      {isGroupDialogOpen && (
        <DialogShell title={editingGroupId ? "Edit Group" : "Create Group"} subtitle="Connections can belong to multiple groups" onClose={closeGroupDialog}>
          <form
            onSubmit={groupForm.handleSubmit(values => {
              saveGroup({
                id: editingGroupId ?? undefined,
                name: values.name,
                description: values.description,
                connectionIds: values.connectionIds,
              });
              closeGroupDialog();
            })}
            className="space-y-3"
          >
            <div className="grid gap-2">
              <input
                {...groupForm.register("name", { required: true })}
                placeholder="Group name"
                className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-indigo-500"
              />
              <input
                {...groupForm.register("description")}
                placeholder="Description (optional)"
                className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-indigo-500"
              />
            </div>
            <div className="max-h-56 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 p-2">
              {state.connections.map(connection => (
                <label key={connection.id} className="flex items-center justify-between rounded-md bg-slate-50 px-2.5 py-2 text-xs text-slate-600">
                  <span className="truncate pr-2">{connection.name} ({platformLabels[connection.platform]})</span>
                  <input type="checkbox" value={connection.id} {...groupForm.register("connectionIds")} className="h-4 w-4 rounded border-slate-200 text-indigo-600" />
                </label>
              ))}
              {state.connections.length === 0 && <p className="px-1 py-2 text-xs text-slate-500">No connections yet.</p>}
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={closeGroupDialog}>Cancel</Button>
              <Button type="submit" size="sm">{editingGroupId ? "Save Group" : "Create Group"}</Button>
            </div>
          </form>
        </DialogShell>
      )}

      {pendingXeroPayload && (
        <DialogShell
          title="Name Xero Connection"
          subtitle={selectedPendingTenant?.tenantName ? `Connected to ${selectedPendingTenant.tenantName}` : "Xero authentication completed"}
          onClose={() => setPendingXeroPayload(null)}
        >
          <form
            onSubmit={pendingConnectionForm.handleSubmit(async values => {
              const tenant = pendingXeroPayload.tenants.find(item => item.tenantId === values.tenantId);
              const nextName = values.name.trim();

              if (!tenant || !nextName) {
                return;
              }

              try {
                await completeOAuthConnection({
                  platform: "xero",
                  name: nextName,
                  tenantId: tenant.tenantId,
                  tenantName: tenant.tenantName,
                  tenantType: tenant.tenantType,
                  oauth: {
                    accessToken: pendingXeroPayload.tokenData.access_token,
                    refreshToken: pendingXeroPayload.tokenData.refresh_token,
                    expiresAt: pendingXeroPayload.tokenData.expires_in
                      ? new Date(Date.now() + pendingXeroPayload.tokenData.expires_in * 1000).toISOString()
                      : undefined,
                    scope: pendingXeroPayload.tokenData.scope,
                    tokenType: pendingXeroPayload.tokenData.token_type,
                    idToken: pendingXeroPayload.tokenData.id_token,
                  },
                });

                setPendingXeroPayload(null);
                pendingConnectionForm.reset({ name: "", tenantId: "" });
              } catch (error) {
                setConnectionError(error instanceof Error ? error.message : "Failed to save Xero connection");
              }
            })}
            className="space-y-3"
          >
            {pendingXeroPayload.tenants.length > 1 && (
              <select
                {...pendingConnectionForm.register("tenantId", { required: true })}
                className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-indigo-500"
              >
                {pendingXeroPayload.tenants.map(tenant => (
                  <option key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.tenantName ?? tenant.tenantId}
                  </option>
                ))}
              </select>
            )}
            <input
              {...pendingConnectionForm.register("name", { required: true })}
              placeholder="Connection name"
              className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-indigo-500"
            />
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              Xero is authenticated. Save this connection name to start using it in chat.
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setPendingXeroPayload(null)}>Cancel</Button>
              <Button type="submit" size="sm">Save Connection</Button>
            </div>
          </form>
        </DialogShell>
      )}

      {renamingConnection && (
        <DialogShell
          title="Rename Connector"
          subtitle={`${platformLabels[renamingConnection.platform]} · ${renamingConnection.tenantId}`}
          onClose={closeRenameDialog}
        >
          <form
             onSubmit={renameForm.handleSubmit(async values => {
               const nextName = values.name.trim();
               if (!nextName) {
                 return;
               }

               try {
                 await renameConnection(renamingConnection.id, nextName);
                 closeRenameDialog();
               } catch (error) {
                 setConnectionError(error instanceof Error ? error.message : "Failed to rename connection");
               }
             })}
             className="space-y-3"
           >
            <input
              {...renameForm.register("name", { required: true })}
              placeholder="Connection name"
              className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-indigo-500"
            />
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={closeRenameDialog}>Cancel</Button>
              <Button type="submit" size="sm">Save</Button>
            </div>
          </form>
        </DialogShell>
      )}

      {disconnectingId && (
        <DialogShell title="Delete Connector" subtitle="This removes the connector from active analysis scopes." onClose={() => setDisconnectingId(null)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Are you sure you want to delete this connector?</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDisconnectingId(null)}>Cancel</Button>
              <Button
                variant="danger"
                size="sm"
                 onClick={async () => {
                   try {
                     await disconnectConnection(disconnectingId);
                     setDisconnectingId(null);
                   } catch (error) {
                     setConnectionError(error instanceof Error ? error.message : "Failed to delete connection");
                   }
                 }}
               >
                Confirm Delete
              </Button>
            </div>
          </div>
        </DialogShell>
      )}

      {deletingGroup && (
        <DialogShell title="Delete Group" subtitle={`Remove ${deletingGroup.name} and unassign its connectors.`} onClose={() => setDeletingGroupId(null)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Are you sure you want to delete this group?</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDeletingGroupId(null)}>Cancel</Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  deleteGroup(deletingGroup.id);
                  setDeletingGroupId(null);
                }}
              >
                Confirm Delete
              </Button>
            </div>
          </div>
        </DialogShell>
      )}
    </div>
  );
};
